import {
  Box,
  Button,
  ButtonGroup,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
  IconButton,
} from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { DialogBox } from '@duke-hq/ui/components/DialogBox/DialogBox';
import { useState, useMemo, useDeferredValue, useCallback } from 'react';
import { ProviderForm } from '../ProviderForm';
import { ProvidersTable } from '../ProvidersTable';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { getZodFieldErrorMessages } from '../../lib/zodErrorMessages';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { ArrowRightFromLine, Plus } from 'lucide-react';
import { ProviderWithContactDetails } from '../ProviderTypes';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useSearchParams } from 'react-router-dom';
import { testIds } from './testIds';
import { withPermission } from '../../withPermission';
import { SortOrder } from '@duke-hq/lib/pagination';
import { useSubSidebar } from '@duke-hq/ui/components/SubSidebar/useSubSidebar';

export type SortFilters = {
  orderBy: string | undefined;
  sort: SortOrder | undefined;
};

const defaultProvider: ProviderWithContactDetails = {
  name: '',
  subdomain: '',
  email_domain: '',
  is_billable: true,
  is_external_id_field_mandatory: false,
  uses_new_referral_process: false,
  contactEmail: '',
  contactFirstName: '',
  contactLastName: '',
  azure_sso_oidc: false,
};

export const ProvidersPage = withPermission('canViewProviders', () => {
  const toast = useToast();
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [csvHasErrors, setCsvHasErrors] = useState<boolean>(false);
  const [csvErrors, setCsvErrors] = useState<string[]>([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [search, setSearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams({
    orderBy: ['name'],
    sort: [SortOrder.ASC],
  });

  const filters: SortFilters = useMemo(() => {
    const orderBy = searchParams.get('orderBy');

    if (!orderBy) {
      // defaults
      return {
        orderBy: 'name',
        sort: SortOrder.ASC,
      };
    }

    return {
      orderBy,
      sort: searchParams.get('sort') === SortOrder.ASC ? SortOrder.ASC : SortOrder.DESC,
    };
  }, [searchParams]);

  const handleSort = useCallback(
    (name: string) => {
      setSearchParams((prev) => {
        const prevOrderBy = prev.get('orderBy') ?? undefined;
        const isSameOrderBy = prevOrderBy === name;
        const orderBy = name ?? prevOrderBy;
        const nextSort = prev.get('sort') === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;

        return {
          ...Object.fromEntries(prev.entries()),
          ...(orderBy && isSameOrderBy
            ? { orderBy, sort: nextSort }
            : { orderBy, sort: SortOrder.ASC }),
        };
      });
    },
    [setSearchParams],
  );

  const { data, isLoading, refetch } = trpcAdmin.providers.all.useQuery(
    {
      search: search ?? '',
      filters,
    },
    {
      placeholderData: (previousData) => previousData,
    },
  );

  const providers = useDeferredValue(data);
  const { isExpanded, toggleExpandSubSidebar } = useSubSidebar();

  const createProvider = trpcAdmin.providers.create.useMutation({
    onSuccess: (data) => {
      toast({
        title: 'Provider created.',
        description:
          'Provider created successfully. It might take up to 60 seconds for the changes to take effect.',
        status: 'success',
        duration: 7000,
        isClosable: true,
      });

      setIsButtonLoading(false);
      onClose();
      void refetch();

      if (data && data.errors.length > 0) {
        setCsvErrors(data.errors);
        setCsvHasErrors(true);
      }
    },
    onError: (error) => {
      const zodErrors = getZodFieldErrorMessages(error);

      toast({
        title: t`Error creating provider`,
        description: zodErrors.length > 0 ? zodErrors.join(' ') : error.message,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });

      setIsButtonLoading(false);
    },
  });

  return (
    <>
      <TableViewLayout
        pageHeader={[
          !isExpanded && (
            <IconButton
              aria-label="expand secondary sidebar"
              icon={<ArrowRightFromLine />}
              variant="tertiary"
              onClick={toggleExpandSubSidebar}
              size="md"
            />
          ),
          <PageHeading key="page-heading" data-testid={testIds.heading}>
            <Trans>Providers</Trans>
          </PageHeading>,
        ]}
        pageActions={
          <Button
            leftIcon={<Icon as={Plus} />}
            size="sm"
            variant="primary"
            onClick={onOpen}
            data-testid={testIds.openCreateProviderModal}
          >
            <Trans>Add a new provider</Trans>
          </Button>
        }
        filters={<SearchInput maxW="search-input" urlKey="q" onCommit={setSearch} />}
        tableActionsLeft={
          <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
            {providers?.length || 0} providers
          </Text>
        }
        table={
          <ProvidersTable
            isLoading={isLoading}
            providers={providers ?? []}
            filters={filters}
            onSort={handleSort}
          />
        }
      />

      <Modal isCentered isOpen={isOpen} onClose={onClose} size={'2xl'}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Create a new provider</Trans>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <ProviderForm
              provider={defaultProvider}
              disabled={isButtonLoading}
              withContact={true}
              withLocations={true}
              onSubmit={async (provider: ProviderWithContactDetails) => {
                setIsButtonLoading(true);
                await createProvider.mutateAsync(provider);
              }}
            ></ProviderForm>
          </ModalBody>
        </ModalContent>
      </Modal>

      <DialogBox
        motionPreset="slideInBottom"
        isCentered
        isOpen={csvHasErrors}
        onClose={() => setCsvHasErrors(false)}
        header={<Trans>Provider locations recorded</Trans>}
        body={
          <Box>
            <Trans>
              Your locations has been saved successfully. However, some entries containing errors
              were not processed. Please review the details below and correct the errors in your
              data.
            </Trans>
            <Box as="ul" mt="10px" mb="20px">
              {csvErrors.map((error: string, idx: number) => (
                <Box as="li" key={idx} fontSize="sm">
                  <Text as="span" fontWeight="bold">
                    {error}
                  </Text>
                </Box>
              ))}
            </Box>
          </Box>
        }
        footer={
          <ButtonGroup>
            <Button
              type="button"
              onClick={() => {
                setCsvHasErrors(false);
              }}
            >
              <Trans>Close</Trans>
            </Button>
          </ButtonGroup>
        }
      />
    </>
  );
});
